import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const shimmer = keyframes`
  0%   { transform: translateX(-100%) rotate(25deg); }
  100% { transform: translateX(200%) rotate(25deg); }
`;

const Button = styled.button`
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 22px;
  border: 1px solid ${props => props.$isLight
    ? 'rgba(0,0,0,0.10)'
    : 'rgba(255,255,255,0.14)'};
  background: ${props => props.$isLight
    ? 'linear-gradient(145deg, rgba(255,255,255,0.92) 0%, rgba(245,245,245,0.88) 60%, rgba(255,255,255,0.90) 100%)'
    : 'linear-gradient(145deg, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0.05) 60%, rgba(255,255,255,0.09) 100%)'};
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow: ${props => props.$isLight
    ? '0 2px 16px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.04)'
    : '0 4px 24px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.08)'};
  color: ${props => props.$isLight ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.85)'};
  cursor: pointer;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -60%;
    width: 40%;
    height: 200%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${props => props.$isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.18)'} 50%,
      transparent 100%
    );
    transform: rotate(25deg);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    border-radius: 22px 22px 0 0;
    background: linear-gradient(180deg,
      ${props => props.$isLight ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.12)'} 0%,
      transparent 100%);
    pointer-events: none;
  }

  &:hover {
    border-color: ${props => props.$isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.22)'};
    background: ${props => props.$isLight
      ? 'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(245,245,245,0.95) 60%, rgba(255,255,255,0.98) 100%)'
      : 'linear-gradient(145deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 60%, rgba(255,255,255,0.13) 100%)'};
    box-shadow: ${props => props.$isLight
      ? '0 6px 24px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,1)'
      : '0 8px 32px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.24), inset 0 -1px 0 rgba(0,0,0,0.1)'};
    color: ${props => props.$isLight ? 'rgba(0,0,0,0.9)' : 'white'};
    transform: translateY(-1px);

    &::before {
      opacity: 1;
      animation: ${shimmer} 0.6s ease forwards;
    }
  }

  &:active {
    transform: translateY(0) scale(0.96);
    box-shadow: ${props => props.$isLight
      ? '0 1px 8px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.8)'
      : '0 2px 12px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.12)'};
  }

  svg {
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 1px 2px ${props => props.$isLight ? 'rgba(0,0,0,0.10)' : 'rgba(0,0,0,0.3)'});
  }
`;

export default function BackButton({ to = '/KMFC/settings' }) {
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  return (
    <Button $isLight={themeMode === 'light'} onClick={() => navigate(to)}>
      <ArrowLeft size={18} strokeWidth={2.2} />
    </Button>
  );
}
